import React, { Component, useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import elementResizeDetectorMaker from 'element-resize-detector';
import moment from 'moment';

import feChangeSchedulerPlaceholder from 'actions/scheduler/feChangeSchedulerPlaceholder.action';
import { feCleanCheckoutData } from 'actions/ui.actions';
import navigateToCheckout from 'bl/checkout/navigateToCheckout.bl';
import filterActiveStylistsWithColumn from 'bl/data/filterActiveStylistsWithColumn.bl';
import { fromPathnameToIsWeek } from 'bl/pathname.bl';
import determineBookingTime from 'bl/scheduler/determineBookingTime.bl';
import fromHoursRangeToExpanded from 'bl/scheduler/fromHoursRangeToExpanded.bl';
import DndContext from 'components/dnd/DndContext.component';
import FLAG from 'config/flag.singleton';
import DELAY from 'constants/delay.const';
import DND from 'constants/dnd.const';
import ELID from 'constants/elid.const';
import ELSEL from 'constants/elsel.const';
import { useTranslation, withGilt, withTranslation } from 'providers/GILT.provider';
import { selectBookingAddedServices } from 'selectors/booking.selectors';
import { selectSchedulerAppointmentDetails } from 'selectors/scheduler.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import { firstOf } from 'utils/arrays/elements.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatYearToDayCompact, formatYearToDayStandard } from 'utils/date/format.util';
import { nowAsTimestamp } from 'utils/date/now.util';
import domElement from 'utils/dom/domElement.util';
import windowScrollToPosition from 'utils/dom/windowScrollTo.util';
import delayed from 'utils/functions/delayed.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseJson from 'utils/parsers/parseJson';
import isFn from 'utils/predicates/function/isFn.util';
import { isEqualByString } from 'utils/predicates/string/simple.util';
import setStateAfterRender from 'utils/react/setStateAfterRender.util';

import { fromProcedureToAddedService } from 'booking/utils';
import SmsButtonNew from 'scheduler/components/common/SmsButtonNew';
import ConfirmationDialog from 'scheduler/components/ConfirmationDialog';
import TimesColumn from 'scheduler/components/TimesColumn.component';
import WeekColumn from 'scheduler/components/WeekColumn';
import WeekColumnHeader from 'scheduler/components/WeekColumnHeader';
import { calculateRangeOfWorkingHoursWeek, generateWeekHeader, rangeHoursNoChange, resizeProcedureDuration } from 'scheduler/utils';

import 'styles/calendar/index.scss';

const L = logger('WeekScheduler');

const ERD = elementResizeDetectorMaker({ strategy: 'scroll' }); // strategy scroll for ultra performance
const RESET_PROCEDURE_ID = -1;

// if a and b are on the same day for this timezone, return true, otherwise return false
const isSameDay = (a, b, tz) => a.tz(tz).day() === b.tz(tz).day();

const DropConfirmationDialog = connect((state) => ({
  appointmentDetails: selectSchedulerAppointmentDetails(state),
  addedServices: selectBookingAddedServices(state),
}))(({ onRequestClose, onRight, totalServices, appointmentDetails, appointmentId, addedServices }) => {
  //
  const appointmentProcedures = getAnyOrVoid([appointmentId, 'response', 'procedures'], appointmentDetails);
  const appointmentResponseState = asObject(appointmentDetails)[appointmentId];
  const isFetchingAppointment = asObject(appointmentResponseState).isFetching;

  const { t } = useTranslation();

  const handleRight = useCallback(
    //
    () => onRight({ addedServices, appointmentProcedures }),
    [addedServices, appointmentProcedures, onRight],
  );
  L.debug('DropConfirmationDialog()', { isFetchingAppointment, appointmentId });

  return (
    <ConfirmationDialog
      onRequestClose={onRequestClose}
      title={t('scheduler.weekScheduler.confirmationModal1.title')}
      disabledRight={isFetchingAppointment}
      leftButtonText={t('scheduler.weekScheduler.confirmationModal1.leftButton')}
      rightButtonText={t('scheduler.weekScheduler.confirmationModal1.rightButton')}
      onLeft={onRequestClose}
      onRight={handleRight}
      onCancel={onRequestClose}
    >
      <p>{t('scheduler.weekScheduler.confirmationModal1.description.part1', { count: totalServices })}</p>
      <p>{t('scheduler.weekScheduler.confirmationModal1.description.part2')}</p>
    </ConfirmationDialog>
  );
});

class WeekScheduler extends Component {
  onMoveProcedure = withOptions(({ time: timeFromOptions, procedure: procedureFromOptions }) => {
    const { updateService, updateProcedure, salonId } = this.props;

    const time = moment(timeFromOptions);
    const procedure = asObject(procedureFromOptions);
    const procedureId = procedure.id;

    L.debug('onMoveProcedure()', procedureId, timeFromOptions, procedureFromOptions);

    const serviceStyle = {
      id: procedureId,
      service: {
        id: procedure.service.id,
        name: procedure.service.name,
        category: procedure.service.category,
        duration: procedure.duration,
        processing: procedure.processing,
        price: procedure.price,
        variable_pricing: false,
        role_executions: [],
      },
      service_id: procedure.service.id,
      category: procedure.service.category.id,
      color: procedure.service.category.color,
      name: procedure.service.name,
      stylist_id: procedure.stylist_id,
      stylist: { id: procedure.stylist_id, user_name: '' },
      price: procedure.price,
      variable_pricing: false,
      duration: procedure.duration,
      processing: procedure.processing,
      start_time: time.toISOString(),
      end_time: moment(time).add(procedure.duration),
      role_executions: [],
    };

    if (procedure.editing && procedure.appointment) {
      serviceStyle.id = procedureId;
      serviceStyle.local_id = procedure.local_id;
      serviceStyle.service_id = procedure.service_id ? procedure.service_id : 0;
      serviceStyle.appointment = procedure.appointment;
      updateService(serviceStyle);
    } else if (procedure.appointment) {
      let newProcedure = { ...procedure };
      if (newProcedure.id < 0) delete newProcedure.id;
      newProcedure = { ...newProcedure, start_time: time.toISOString() };

      L.unignore('onMoveProcedure()', 'updateProcedure:', updateProcedure(salonId, newProcedure));
    }
  });
  onDeleteEvent = withOptions((options) => {
    const { event, all } = L.debug('onDeleteEvent()', 'options:', options);
    const { t } = this.props;

    const Modal = ({ onRequestClose, e, createUpdateTimeoff }) => (
      <ConfirmationDialog
        onRequestClose={onRequestClose}
        title={t('scheduler.weekScheduler.confirmationModal2.title')}
        leftButtonText={t('scheduler.weekScheduler.confirmationModal2.leftButton')}
        onLeft={() => createUpdateTimeoff({ ...e, update_all: true }, 'DELETE').then(onRequestClose)}
        rightButtonText={t('scheduler.weekScheduler.confirmationModal2.rightButton')}
        onRight={() => createUpdateTimeoff({ ...e, update_all: false }, 'DELETE').then(onRequestClose)}
        onCancel={onRequestClose}
      >
        <p>{t('scheduler.weekScheduler.confirmationModal2.description1')}</p>
        <p>{t('scheduler.weekScheduler.confirmationModal2.description2')}</p>
      </ConfirmationDialog>
    );
    if (event.recurrences) {
      this.props.showModal(Modal, {
        e: event,
        createUpdateTimeoff: this.props.createUpdateTimeoff,
      });
    } else {
      this.props.createUpdateTimeoff({ ...event, update_all: all }, 'DELETE');
    }
  });

  filterAddedServices = alwaysSame((isPosDeposit, booking, stylistId) => (isPosDeposit ? [] : booking.addedServices.filter((s) => s.stylist_id === stylistId && !s.deleted)));

  filterNoNewlyAdded = alwaysSame((allProceduresForThisDay, addedServicesIds, deletedServicesIds) =>
    allProceduresForThisDay.filter((p) => !addedServicesIds.includes(p?.id) && !deletedServicesIds.includes(p?.id)),
  );

  filterWeekProcedures = alwaysSame((list, day, stylistId) => {
    const hideNoShow = this.props.hideNoShow;

    return list.filter((procedure) => {
      const candidateId = procedure?.stylist?.id;
      const status = procedure?.appointment?.status;
      const bgn = procedure?.start_time;

      return !(hideNoShow && status === 'canceled') && !(hideNoShow && status === 'no_show') && formatYearToDayStandard(bgn) === day && candidateId === stylistId;
    });
  });

  filterWeekEvents = alwaysSame((list, date, stylistId, timeZone) =>
    list.filter(
      (event) =>
        moment(event.end_time).tz(timeZone).isAfter(moment(date).startOf('day')) && moment(event.start_time).tz(timeZone).isBefore(moment(date).endOf('day')) && event.stylist_id === stylistId,
    ),
  );

  constructor(props) {
    super(props);

    this.scrollRef = React.createRef();
    this.currentTimeRef = React.createRef();
    this.selectedStylistRef = React.createRef();

    this.state = {
      procedureId: -1,
      movedProcedureIDs: [0], // eslint-disable-next-line react/no-unused-state
      width: 0,
      rangeByHours: [],
      currentActiveProcedure: 0,
      currentModalAction: props.location.state && props.location.state.showModalAction ? props.location.state.showModalAction : null,
      lastRanModalAction: null,
      timeoutHandle: null,
      showSms: false,
      showMoveNotification: false,
    };
    let maxWeekRange;
    if (!this.state.rangeByHours.length) {
      const week = generateWeekHeader(props.selectedDate, props.openingHours, props.formatDate);
      const rangeByHoursWeekAll = week.map((day) => calculateRangeOfWorkingHoursWeek(props.openingHours, day.wholeDate, 15, props.procedures));
      const maxRangeIndex = rangeByHoursWeekAll.reduce((acc, cur, idx, src) => (cur.length > src[acc].length ? idx : acc), 0);
      maxWeekRange = rangeByHoursWeekAll[maxRangeIndex];
      this.state.rangeByHours = maxWeekRange;
      if (!rangeHoursNoChange(this.state.rangeByHours, maxWeekRange)) {
        this.setState({ rangeByHours: maxWeekRange });
      }
    } else if (maxWeekRange && !rangeHoursNoChange(this.state.rangeByHours, maxWeekRange)) {
      this.setState({ rangeByHours: maxWeekRange });
    }
  }

  static getDerivedStateFromProps(nextProps) {
    const changedState = {};
    if (nextProps.location.state && nextProps.location.state.showModalAction) {
      changedState.currentModalAction = nextProps.location.state.showModalAction;
    }

    return changedState;
  }

  componentDidMount() {
    const activeStylistsWithColumn = filterActiveStylistsWithColumn(this.props.displayStylists);

    const offsetTop = this.currentTimeRef?.current?.offsetTop;
    const clientWidth = this.scrollRef?.current?.clientWidth;

    this.scrollToTodayColumn();

    const schedulerScrollEl = domElement(ELSEL.schedscroll);
    this.schedulerScrollEl = schedulerScrollEl;

    if (offsetTop) {
      if (schedulerScrollEl) schedulerScrollEl.scrollTop = offsetTop;

      if (clientWidth) {
        // eslint-disable-next-line react/no-did-mount-set-state
        ERD.listenTo(schedulerScrollEl, this.updateWidth);
      }
    }

    if (activeStylistsWithColumn?.length === 1) {
      this.tryToFireModalAction();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { procedureId } = this.state;
    const activeStylistsWithColumn = filterActiveStylistsWithColumn(this.props.displayStylists);

    if (snapshot) {
      if (this.selectedStylistRef && this.selectedStylistRef.current && this.selectedStylistRef.current.offsetLeft) {
        this.schedulerScrollEl.scrollLeft = snapshot;
      }
    }

    if (activeStylistsWithColumn?.length === 1) {
      this.tryToFireModalAction();
    }
    if (this.props.messageSent) {
      setTimeout(() => {
        this.showSmsPopup(false);
      }, 2000);
    }

    if (procedureId > 0 && prevState.procedureId !== procedureId) {
      this.setState({ showMoveNotification: true });

      clearTimeout(this.hideMoveTimer);
      this.hideMoveTimer = delayed(DELAY.autohide2, () => {
        this.setState({ showMoveNotification: false });
      });
    }
  }

  componentWillUnmount = () => {
    ERD.removeListener(this.schedulerScrollEl);
    clearTimeout(this.hideMoveTimer);
  };

  onCheckout = (appointment) =>
    void navigateToCheckout({
      appointment,
      history: this.props.history,
      isWeek: fromPathnameToIsWeek(this.props.location?.pathname),
      onClean: this.props.feCleanCheckoutData,
      onData: this.props.setCheckoutInitData,
      pdi: this.props.procedures_date_index,
      procedures: this.props.procedures,
      salonId: this.props.salonId,
      stylistId: this.props.stylistId,
      tag: 'WeekScheduler.onCheckout()',
    });

  onResizeProcedure = (procedure) => {
    if (procedure.editing && procedure.appointment) {
      this.props.updateService(fromProcedureToAddedService(procedure));
    } else {
      this.props.updateProcedure(this.props.salonId, procedure);
    }
  };

  onMoveEvent = (time, stylistId, event, extend = false) => {
    const { t } = this.props;

    let newEvent;

    if (extend) {
      let endTime = time.toISOString();
      if (!moment(time).isAfter(moment(event.start_time))) {
        endTime = moment(event.start_time).add(15, 'minutes');
      }
      newEvent = {
        ...event,
        end_time: endTime,
      };
    } else {
      newEvent = {
        ...event,
        stylist_id: stylistId,
        start_time: time.toISOString(),
        end_time: moment(time)
          .add(moment(event.end_time).diff(moment(event.start_time)))
          .toISOString(),
      };
    }

    if (newEvent.client === null) {
      delete newEvent.client;
    }

    const Modal = ({ onRequestClose, e, createUpdateTimeoff }) => (
      <ConfirmationDialog
        onRequestClose={onRequestClose}
        title={t('scheduler.weekScheduler.confirmationModal3.title')}
        leftButtonText={t('scheduler.weekScheduler.confirmationModal3.leftButton')}
        onLeft={() => createUpdateTimeoff({ ...e, update_all: true }, 'PUT', event).then(onRequestClose)}
        rightButtonText={t('scheduler.weekScheduler.confirmationModal3.rightButton')}
        onRight={() => createUpdateTimeoff({ ...e, update_all: false }, 'PUT', event).then(onRequestClose)}
        onCancel={onRequestClose}
      >
        <p>{t('scheduler.weekScheduler.confirmationModal3.description1')}</p>
        <p>{t('scheduler.weekScheduler.confirmationModal3.description2')}</p>
      </ConfirmationDialog>
    );

    if (newEvent.recurrences) {
      this.props.showModal(Modal, { e: newEvent, createUpdateTimeoff: this.props.createUpdateTimeoff });
    } else {
      this.props.createUpdateTimeoff(newEvent, 'PUT', event);
    }
  };

  onDragStart = (ev) => {
    const { fetchAppointmentDetails, salonId } = this.props;

    if (isFn(window.navigator.vibrate)) window.navigator.vibrate(200);

    const idDrag = parseJson(ev?.draggableId);
    const idDragType = idDrag?.type;
    const appointmentId = idDrag?.procedure?.appointment?.id;

    if (DND.dragProcedure === idDragType && appointmentId) {
      L.unignore('onDragStart()', 'fetchAppointmentDetails:', fetchAppointmentDetails(appointmentId, salonId));
    }
  };

  onDragEnd = (ev) => {
    const activeStylistsWithColumn = filterActiveStylistsWithColumn(this.props.displayStylists);

    const { timeoutHandle } = this.state;
    const { trackEvent, setDropTime, showModal, procedures, timezone, feChangeSchedulerPlaceholder, t } = this.props;

    const elProcedureModal = document.querySelector(`#${ELID.bookside}`);

    const idDrag = parseJson(ev?.draggableId ?? null);
    const idDropTarget = parseJson(ev?.destination?.droppableId ?? null);

    const { stylist, time, type: idDropTargetType } = idDropTarget ?? {};
    const { event, procedure, type: idDragType } = idDrag ?? {};
    const { id: procedureId, appointment, start_time: procedureStart } = procedure ?? {};
    const { id: appointmentId } = appointment ?? {};
    const { id: stylistId } = stylist ?? {};

    const ap = this.props.appointmentPerms;
    if (ap?.isMoveBlocked && idDragType === DND.dragProcedure) return;
    if (ap?.isMovePersonalOnly && idDragType === DND.dragProcedure && stylistId !== ap.accessStylistId) return;

    const dropSingle = () => {
      // reset procedureId
      setStateAfterRender(this, { procedureId: RESET_PROCEDURE_ID });
      setStateAfterRender(this, { procedureId });
      if (activeStylistsWithColumn?.length === 1 && !elProcedureModal) {
        setDropTime(nowAsTimestamp());
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          setStateAfterRender(this, { timeoutHandle: 0 });
        }
        setStateAfterRender(this, { timeoutHandle: setTimeout(() => void setDropTime(0), 5000) });
      }
      this.onMoveProcedure({ time, procedure });
    };

    if (idDragType === DND.dragProcedure && idDropTargetType === DND.dropCell) {
      trackEvent('WeekSchedulerProcedureDragEnd');

      const filteredServices = procedures.filter((p) => p.appointment.id === appointmentId);
      const totalServices = filteredServices.length;

      if (isSameDay(moment(time), moment(procedureStart), timezone)) {
        // same day -> directly move without modal
        dropSingle();
      } else if (totalServices > 1) {
        //different day and more than 1 service -> show modal
        const Modal = ({ onRequestClose }) => {
          const dropMultiple = withOptions(({ delta, procedures }) => {
            L.debug('onDragEnd()', 'dropMultiple()', { delta, procedures });
            for (const p of asArray(procedures)) {
              L.debug('onDragEnd()', 'dropMultiple()', p);
              const { start_time: t, editing, stylist: s } = p;
              this.onMoveProcedure({
                time: moment(t).subtract(delta),
                procedure: { ...p, editing, stylist_id: s?.id },
              });
            }
          });

          return (
            <DropConfirmationDialog
              appointmentId={appointmentId}
              onRequestClose={onRequestClose}
              onRight={({ addedServices, appointmentProcedures }) => {
                const delta = moment(procedureStart).diff(moment(time));
                dropSingle();
                if (asArray(addedServices).length > 0) {
                  dropMultiple({ delta, procedures: addedServices });
                } else {
                  dropMultiple({ delta, procedures: appointmentProcedures });
                  setStateAfterRender(this, { movedProcedureIDs: asArray(appointmentProcedures).map((p) => p?.id) });
                }
                onRequestClose();
              }}
              totalServices={totalServices}
            />
          );
        };
        showModal(Modal, {});
      } else if (totalServices === 0) {
        // when can not determine services
        const Modal = ({ onRequestClose }) => (
          <ConfirmationDialog
            onRequestClose={onRequestClose}
            title={t('scheduler.weekScheduler.confirmationModal4.title')}
            leftButtonText=""
            onLeft={onRequestClose}
            rightButtonText={t('scheduler.weekScheduler.confirmationModal4.rightButton')}
            onRight={onRequestClose}
            onCancel={onRequestClose}
          >
            <p>{t('scheduler.weekScheduler.confirmationModal4.description1')}</p>
            <p>{t('scheduler.weekScheduler.confirmationModal4.description2')}</p>
          </ConfirmationDialog>
        );
        showModal(Modal, {});
      } else {
        // different day, 1 service -> directly move without modal
        dropSingle();
      }
    } else if (idDragType === DND.resizeProcedure && idDropTargetType === DND.dropCell) {
      trackEvent('WeekSchedulerProcedureResizeDragEnd');
      this.onResizeProcedure(resizeProcedureDuration({ procedure, endTime: time }));
    } else if (idDragType === DND.dragEvent && idDropTargetType === DND.dropCell) {
      trackEvent('WeekSchedulerEventDragEnd');
      this.onMoveEvent(moment(time), stylistId, event);
    } else if (idDragType === DND.resizeEvent && idDropTargetType === DND.dropCell) {
      trackEvent('WeekSchedulerEventResizeDragEnd');
      this.onMoveEvent(moment(time), 0, event, true);
    } else if (idDragType === DND.dragPlaceholder && idDropTargetType === DND.dropCell) {
      feChangeSchedulerPlaceholder({ stylistId, time });
    }
  };

  updateWidth = () => {
    if (this.scrollRef && this.scrollRef.current && this.scrollRef.current.clientWidth) {
      // eslint-disable-next-line react/no-unused-state
      this.setState({ width: this.scrollRef.current.clientWidth });
    }
  };

  markProcedures = (appointmentId, procedureId, mark) => {
    this.setState({ currentActiveProcedure: procedureId });
    this.props.markAppointmentEdit(appointmentId, mark);
  };

  notifyClient = () => void this.setState({ showSms: true });

  showSmsPopup = (tf) => void this.setState({ showSms: tf });

  /**
   * This function will try to show appoinment/ procedure's modal, most probably clicked from the client profile
   * The idea is that a parameter is passed via the router ( from the client profile) and this parameter is the
   * "action". The first thing that is done is to check if the appointment's date is the current calendar date,
   * if it is not change the date and once it is updated it will again come to this place but this time with
   * the right date, once that is done it will try to find the appointment by id. The id is set in the
   * SchedulerProcedure.jsx as `appointment_${appointment.id}` and if it finds an element like that it will
   * trigger the click event on it and that automatically will trigger all other logic about showing the modal.
   *
   * - If it doesn't find an element with the id passed the action will stay there until it finds the element
   * (on component update) OR the action changes
   * - If it finds the element and everything is fine the "lastRanModalAction" prop of the state is set so the
   * action is not done twice.
   */
  tryToFireModalAction = () => {
    if (this.state.currentModalAction && (!this.state.lastRanModalAction || this.state.lastRanModalAction.definedTimestamp !== this.state.currentModalAction.definedTimestamp)) {
      if (this.props.selectedDate.format('YYYY-MM-DD') !== moment(this.state.currentModalAction.dateToSwitchTo).format('YYYY-MM-DD')) {
        const mDate = moment(this.state.currentModalAction.dateToSwitchTo);
        this.props.onChangeDate(mDate);
        this.setState({ lastRanModalAction: this.state.currentModalAction });

        setTimeout(() => {
          if (this.state.currentModalAction) {
            const elString = `appointment_${this.state.currentModalAction.appointmentId}`;
            const el = document.getElementById(elString);
            if (el) {
              el.click();
            }
          }
        }, 1500);
      }
    }
  };

  undoProcedure = () => {
    this.setState({ showMoveNotification: false });
    if (this.state.movedProcedureIDs.length === 1) {
      this.props.undoProcedure(this.props.salonId, this.state.procedureId);
    } else if (this.state.movedProcedureIDs.length > 0) {
      for (let i = 0; i < this.state.movedProcedureIDs.length; i += 1) {
        this.props.undoProcedure(this.props.salonId, this.state.movedProcedureIDs[i]);
      }
    }
  };

  scrollToTodayColumn = () => {
    const columnSelector = domElement(`#${ELID.todayColumn}`);

    if (!columnSelector) {
      return;
    }

    delayed(DELAY.attention, () => {
      const elementPosition = columnSelector.getBoundingClientRect();
      const offsetLeft = window.scrollX + elementPosition.left;
      const timeColumnWith = 50; // px
      windowScrollToPosition(offsetLeft - timeColumnWith, 0);
    });
  };

  defineWeek = () => generateWeekHeader(this.props.selectedDate, this.props.openingHours, this.props.formatDate);

  render() {
    const {
      booking,
      stylistId,
      openingHours,
      selectedDate,
      procedures,
      timezone,
      isPosDeposit,
      updateProcedure,
      showModal,
      pickCurrentStylist,
      history,
      markAppointmentEdit,
      hovered,
      salonId,
      stylists,
      updateProcedureStatus,
      location,
      isOneStylist,
      events,
      categoriesAndServices,
      procedureDragFrom,
      clientList,
      t,
    } = this.props;
    const { rangeByHours, procedureId } = this.state;

    const hasProcedureId = procedureId > 0;
    const movedProcedure = hasProcedureId ? firstOf(procedures.filter((p) => p?.id === procedureId)) : void 1;

    const client = movedProcedure?.appointment?.client;
    const clientId = hasProcedureId ? client?.id : void 1;
    const clientPhone = clientList?.find((c) => c.id === clientId)?.phone || '';

    const week = this.defineWeek();

    const addedServicesIds = booking.addedServices.filter((o) => !o.deleted).map((o) => o?.id);
    const deletedServicesIds = booking.addedServices.filter((o) => o.deleted).map((o) => o?.id);

    const stylist4Col = firstOf(asArray(this.props.stylists).filter((s) => isEqualByString(s?.id, stylistId)));

    const expandedRange = FLAG.expandDayOffHours ? fromHoursRangeToExpanded(rangeByHours) : rangeByHours;
    const currentActiveProcedure4Col = this.state.currentActiveProcedure;

    return (
      <div data-bem="WeekScheduler" ref={this.scrollRef} className="seven-columns-in-week column-in-viewport">
        <DndContext tag="WeekScheduler--dnd-context" onDragEnd={this.onDragEnd} onDragStart={this.onDragStart}>
          <div className="WEEK-VIEW the-scheduler-if-self">
            <div data-bem="WeekScheduler__times-column" className="hours">
              <TimesColumn range={expandedRange} currentTimeRef={this.currentTimeRef} tz={timezone} />
            </div>

            {week.map((day, index) => {
              const dayWholeDate4Col = fromAnyDateToIso(day.wholeDate);
              const wholeDateFormat1 = formatYearToDayStandard(dayWholeDate4Col);
              const wholeDateFormat2 = formatYearToDayCompact(dayWholeDate4Col);

              const addedServices4Col = this.filterAddedServices(isPosDeposit, booking, stylistId);
              const bookingTime4Col = determineBookingTime({ booking, stylistId });
              const events4Col = this.filterWeekEvents(events, dayWholeDate4Col, stylistId, timezone);
              const allProcedures4Col = this.filterWeekProcedures(procedures, wholeDateFormat1, stylistId);
              const filteredNoNewlyAddedProcedures4Col = this.filterNoNewlyAdded(allProcedures4Col, addedServicesIds, deletedServicesIds);
              const newEntryIndicator4Col = fromAnyToBool(booking && booking.currentStylistId === stylistId && !addedServicesIds.length);

              return (
                <div
                  data-bem="WeekScheduler__day"
                  data-day={wholeDateFormat1}
                  key={day.date}
                  className={cn({
                    'column-container': true,
                    'column-off-container': !day.open || moment().format('YYYYMMDD') === wholeDateFormat2,
                    'day-off': !day.open,
                  })}
                >
                  <WeekColumnHeader
                    tag={`WeekScheduler__header--${index + 1}`}
                    addedServices={booking.addedServices}
                    date={day.date}
                    dateFormat={wholeDateFormat2}
                    dayName={day.name}
                    history={history}
                    oneStylist={isOneStylist}
                    salonId={salonId}
                  />

                  <WeekColumn
                    key={day.id}
                    tag={`WeekScheduler__column--${index + 1}`}
                    addedServices={addedServices4Col}
                    allProcedures={allProcedures4Col}
                    bookingTime={bookingTime4Col}
                    currentActiveProcedure={currentActiveProcedure4Col}
                    dayWholeDate={dayWholeDate4Col}
                    events={events4Col}
                    newEntryIndicator={newEntryIndicator4Col}
                    filteredNoNewlyAddedProcedures={filteredNoNewlyAddedProcedures4Col}
                    stylist={stylist4Col}
                    stylistId={stylistId || stylist4Col?.id}
                    categoriesAndServices={categoriesAndServices}
                    history={history}
                    hovered={hovered}
                    location={location}
                    markAppointmentEdit={markAppointmentEdit}
                    markProcedures={this.markProcedures}
                    onCheckout={this.onCheckout}
                    onDeleteEvent={this.onDeleteEvent}
                    openingHours={openingHours}
                    pickCurrentStylist={pickCurrentStylist}
                    rangeByHours={expandedRange}
                    salonId={salonId}
                    salonTimezone={timezone}
                    selectedDate={selectedDate}
                    showModal={showModal}
                    stylists={stylists}
                    updateProcedure={updateProcedure}
                    updateProcedureStatus={updateProcedureStatus}
                    appointmentPerms={this.props.appointmentPerms}
                  />
                </div>
              );
            })}
          </div>
        </DndContext>

        {this.state.showMoveNotification && clientId && (
          <div className={clientId ? 'move-notification' : 'move-notification two-btn'}>
            <div className="moved-btn">{t('scheduler.weekScheduler.appointmentMoved')}</div>
            {clientPhone && (
              <div className="notify-btn" role="button" tabIndex={0} onClick={this.notifyClient}>
                {t('scheduler.weekScheduler.confirmChangeButton')}
              </div>
            )}
            <div className="undo-btn" role="button" tabIndex={-1} onClick={this.undoProcedure}>
              {t('scheduler.weekScheduler.undoMoveButton')}
            </div>
          </div>
        )}
        {this.state.showSms && (
          <SmsButtonNew
            procedure={movedProcedure}
            bucketType="appointment"
            smsComposeModalOpen={this.state.showSms}
            showParentSmsPopup={this.showSmsPopup}
            stylists={stylists}
            moveNotify
            procedureDragFrom={procedureDragFrom}
          />
        )}
      </div>
    );
  }
}

const WeekSchedulerHoc = connect(null, mapActionsToDispatchToProps({ feChangeSchedulerPlaceholder, feCleanCheckoutData }))(withGilt(withTranslation()(WeekScheduler)));

export default WeekSchedulerHoc;
